import { ReactRenderer } from '@tiptap/react';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import { MentionList, MentionListRef } from './MentionList';
import { MentionUser, SuggestionProps } from './Mention';
import { memberApi } from '@/lib/api/members.api';
import { MemberStatusEnum } from '@scrambl/shared';
import { createLogger } from '@/lib/logger';

const logger = createLogger('components/notes/extensions/suggestion');

// Cache for mention users to avoid repeated API calls
const mentionUsersCache = new Map<string, { users: MentionUser[]; timestamp: number }>();
const CACHE_DURATION = 60000; // 1 minute cache

interface MentionSuggestionContext {
  moduleType?: string;
  moduleId?: string;
}

/**
 * The record a mention list is scoped to isn't always known when the editor is
 * created — a task being added inline has no id until it's saved. Tiptap bakes
 * extension options in at editor creation and never re-reads them, so callers
 * pass a getter and we resolve the scope on every lookup instead. Without this
 * the composer keeps asking for an unscoped list forever, and client portal
 * users (which the server only returns for a known task/project) never appear
 * until the page is reloaded.
 */
type MentionSuggestionContextInput =
  | MentionSuggestionContext
  | (() => MentionSuggestionContext | undefined);

function resolveContext(
  input?: MentionSuggestionContextInput,
): MentionSuggestionContext | undefined {
  const context = typeof input === 'function' ? input() : input;
  if (!context) return undefined;

  // An empty-string id is "not scoped yet", not a record — sending it would
  // cache an unscoped list under a key we'd never revisit.
  const moduleId = context.moduleId || undefined;
  const moduleType = context.moduleType || undefined;

  return moduleType || moduleId ? { moduleType, moduleId } : undefined;
}

function getCacheKey(context?: MentionSuggestionContext): string {
  return `${context?.moduleType ?? 'default'}:${context?.moduleId ?? 'default'}`;
}

/**
 * Fetch organization members for mention suggestions
 */
async function fetchMentionUsers(context?: MentionSuggestionContext): Promise<MentionUser[]> {
  const now = Date.now();
  const cacheKey = getCacheKey(context);
  const cached = mentionUsersCache.get(cacheKey);

  // Return cached data if still valid
  if (cached && now - cached.timestamp < CACHE_DURATION) {
    return cached.users;
  }

  try {
    const response = await memberApi.getMentionSuggestions(context);
    const users: MentionUser[] = response.data;

    // Update cache
    mentionUsersCache.set(cacheKey, { users, timestamp: now });

    return users;
  } catch (error) {
    logger.error('Failed to load mention users:', error);
    if (cached) {
      return cached.users;
    }

    try {
      const response = await memberApi.getMembersForDisplay({
        status: MemberStatusEnum.ACTIVE,
        limit: 100,
      });

      return response.data.map(member => {
        const user = member.user;
        const displayName =
          user.displayName ||
          (user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : null) ||
          user.firstName ||
          user.email;

        return {
          id: member.userId,
          name: displayName,
          email: user.email,
          avatarUrl: user.image,
          source: 'internal',
        };
      });
    } catch (fallbackError) {
      logger.error('Failed to load fallback mention users:', fallbackError);
      return [];
    }
  }
}

/**
 * Filter users based on query string
 */
function filterUsers(users: MentionUser[], query: string): MentionUser[] {
  const lowerQuery = query.toLowerCase();
  const matches = users
    .map((user, index) => {
      const name = user.name.toLowerCase();
      const email = user.email.toLowerCase();
      const nameIndex = name.indexOf(lowerQuery);
      const emailIndex = email.indexOf(lowerQuery);

      return {
        user,
        index,
        rank:
          name === lowerQuery
            ? 0
            : name.startsWith(lowerQuery)
              ? 1
              : nameIndex >= 0
                ? 2
                : email.startsWith(lowerQuery)
                  ? 3
                  : emailIndex >= 0
                    ? 4
                    : 5,
      };
    })
    .filter(match => match.rank < 5)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)

  return (['internal', 'external', 'portal'] as const)
    .flatMap(source =>
      matches
        .filter(match => (match.user.source ?? 'internal') === source)
        .slice(0, 8)
        .map(match => match.user),
    );
}

/**
 * Create the suggestion configuration for the Mention extension
 */
export function createSuggestion(context?: MentionSuggestionContextInput) {
  return {
    items: async ({ query }: { query: string }): Promise<MentionUser[]> => {
      const users = await fetchMentionUsers(resolveContext(context));
      return filterUsers(users, query);
    },

    render: () => {
      let component: ReactRenderer<MentionListRef> | null = null;
      let popup: TippyInstance[] | null = null;

      return {
        onStart: (props: SuggestionProps) => {
          component = new ReactRenderer(MentionList, {
            props: {
              items: props.items,
              command: props.command,
            },
            editor: props.editor as any,
          });

          if (!props.clientRect) {
            return;
          }

          popup = tippy('body', {
            getReferenceClientRect: props.clientRect as () => DOMRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
            theme: 'mention-dropdown',
            animation: 'shift-away',
            maxWidth: 280,
          });
        },

        onUpdate(props: SuggestionProps) {
          if (!component) {
            return;
          }

          component.updateProps({
            items: props.items,
            command: props.command,
          });

          if (!popup?.[0] || !props.clientRect) {
            return;
          }

          popup[0].setProps({
            getReferenceClientRect: props.clientRect as () => DOMRect,
          });
        },

        onKeyDown(props: { event: KeyboardEvent }): boolean {
          if (props.event.key === 'Escape') {
            popup?.[0]?.hide();
            return true;
          }

          return component?.ref?.onKeyDown(props) || false;
        },

        onExit() {
          popup?.[0]?.destroy();
          component?.destroy();
          popup = null;
          component = null;
        },
      };
    },
  };
}

/**
 * Clear the mention users cache (useful when org members change)
 */
export function clearMentionCache() {
  mentionUsersCache.clear();
}

/**
 * Drop the cached list for one record. Who is suggestible on a task depends on
 * the task itself — a client portal user is only offered once the task sits in
 * a group shared with them — so editing a task has to invalidate its entry,
 * otherwise the mention list stays wrong until the entry ages out.
 */
export function clearMentionCacheFor(moduleType?: string, moduleId?: string) {
  mentionUsersCache.delete(getCacheKey({ moduleType, moduleId }));
}
